/**
 * AddAsiModal — create a new ASI (Architect's Supplemental Instruction, Supabase),
 * scoped to a contract.
 *
 * ASIs are ARCHITECT-DRIVEN construction-support items (Round 7): the firm originates
 * them and runs them through a set milestone workflow/checklist. On create, this modal
 * creates a MILESTONE from the 'Architects Supplemental Instructions' deliverable
 * template (Construction Support phase, with its checklist) and stores its Airtable ID
 * as milestone_airtable_id. It is created UNOWNED — the deliverable type's default
 * Responsible/Accountable/Supportive are no longer stamped onto it (RASCI retired
 * 2026-09-02, docs/FLOW_RECONCILIATION_PLAN.md); it gets a crew when the tower launches
 * it on the Flight Board. Outside-driven items (RFIs, Submittals) remain thread-linked;
 * legacy ASIs keep their thread_airtable_id.
 *
 * Create-only (Round 22) — editing an existing ASI's fields (subject, sender, dates,
 * description, bound PDF attachments) happens inline on the ASI's own child rows in
 * ConstructionSection.jsx (AsiDetailRows/AsiAttachmentsRow), not through this modal. The issued
 * PDF itself (document_url) is never user-uploaded — it's generated at issue/reissue time.
 *
 * Opens via CustomEvent('open-add-asi-modal', { detail: { contractId, projectId } }).
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { createAsi, getNextAsiNumber } from '../services/asis.js'
import { getMilestoneTemplates, createMilestone } from '../services/milestones.js'
import { seedMilestoneChecklist } from '../services/checklist.js'
import { getTeamMembers } from '../services/members.js'
import { ASI_TEMPLATE_ID } from '../outline/constants.js'
import MarkdownField from './MarkdownField.jsx'

function isoDate(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

/**
 * Create the ASI's milestone from the deliverable template — same payload as
 * AddMilestoneWizard (createMilestone + checklist scaffold; no assignment).
 * Returns the new milestone Airtable ID, or null on any failure (non-fatal).
 */
async function createAsiMilestone(projectId, asiNumber, subject) {
  const tpls = await getMilestoneTemplates()
  const tpl = (tpls || []).find(t => t.id === ASI_TEMPLATE_ID)
  if (!tpl) return null

  const csPhase = (ctx.state?.phases || []).find(p => ((p.fields['Phase Name']) || '').indexOf('Construction Support') !== -1)
  const phaseName = csPhase?.fields?.['Phase Name'] || 'Construction Support Phase'

  const today = new Date()
  const lftDate = new Date(today)
  lftDate.setDate(lftDate.getDate() + 7)
  const defDur = Math.max(1, Math.ceil(parseFloat(tpl.fields['Default Days Budgeted'] || 1)))

  const delFields = {
    'Project':               [projectId],
    'Deliverable':           [tpl.id],
    'Project Phase':         phaseName,
    'Latest Finish (LFT)':   isoDate(lftDate),
    'Earliest Start (EST)':  isoDate(today),
    'Duration':              defDur,
    'Created by Automation': true,
    'Create Slack Thread':   true,
    'Subtitle':              (['ASI', asiNumber].filter(Boolean).join(' ') + (subject ? ' — ' + subject : '')).trim(),
  }
  if (csPhase) delFields['Phase'] = [csPhase.id]

  const newId = await createMilestone(delFields)

  // Checklist scaffold from the template (non-fatal)
  try {
    await seedMilestoneChecklist(newId)
  } catch (_) { /* non-fatal */ }

  return newId
}

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

export default function AddAsiModal() {
  const [open, setOpen]             = useState(false)
  const [contractId, setContractId] = useState(null)
  const [projectId, setProjectId]   = useState(null)
  const [asiNumber, setAsiNumber]   = useState('')
  const [subject, setSubject]       = useState('')
  const [issueDate, setIssueDate]   = useState('')
  const [description, setDescription] = useState('')
  const [attachments, setAttachments] = useState('')
  const [senderId, setSenderId]     = useState('')
  const [members, setMembers]       = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setContractId(d.contractId || null)
      setProjectId(d.projectId || null)
      setAsiNumber('')
      setSubject('')
      setIssueDate('')
      setDescription('')
      setAttachments('')
      setSenderId(ctx.currentMember?.id || '')
      setError(null); setSubmitting(false); setOpen(true)
      // Auto-number: suggest the next ASI # in sequence (still editable — a drafter working out
      // of order can override it).
      getNextAsiNumber(d.projectId, d.contractId).then(n => setAsiNumber(String(n))).catch(() => {})
      if (members === null) getTeamMembers().then(setMembers).catch(() => setMembers([]))
    }
    window.addEventListener('open-add-asi-modal', onOpen)
    return () => window.removeEventListener('open-add-asi-modal', onOpen)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    if (!contractId) { setError('No contract in context.'); return }
    setSubmitting(true); setError(null)
    const senderMember = (members || []).find(m => m.id === senderId)
    const fields = {
      asi_number: asiNumber || null,
      subject: subject || null,
      issue_date: issueDate || null,
      detailed_description: description || null,
      attachment_list: attachments || null,
      sender_member_id: senderId || null,
      sender_name: senderMember?.name || null,
      status: 'draft',
    }
    try {
      // ASIs are architect-driven: create the ASI milestone (template + checklist) in
      // Construction Support and link it. Non-fatal — the ASI is still created if
      // milestone scaffolding fails.
      let milestoneId = null
      if (projectId) {
        try { milestoneId = await createAsiMilestone(projectId, asiNumber, subject) } catch (_) { /* non-fatal */ }
      }
      await createAsi(contractId, projectId, { ...fields, milestone_airtable_id: milestoneId })
      // Reload project data so the new milestone appears in the schedule
      if (milestoneId && projectId && ctx.reloadProjectData) ctx.reloadProjectData(projectId)
      if (ctx.asiCache) ctx.asiCache[contractId] = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save ASI'); setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddAsiModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 480, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Add ASI</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>ASI #</label>
              <input style={inputStyle} value={asiNumber} onChange={e => setAsiNumber(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Issue Date</label>
              <input type="date" style={inputStyle} value={issueDate} onChange={e => setIssueDate(e.target.value)} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Subject</label>
            <input style={inputStyle} value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Sender</label>
            <select style={inputStyle} value={senderId} onChange={e => setSenderId(e.target.value)}>
              <option value="">— Select team member —</option>
              {(members || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Detailed Description</label>
            <MarkdownField value={description} onChange={setDescription} />
          </div>
          <div>
            <label style={labelStyle}>Attachment List</label>
            <MarkdownField value={attachments} onChange={setAttachments} />
          </div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>
            Add supplemental PDF attachments once it's created — expand it and use "+ Add PDF
            attachment(s)" under Bound Attachments. They'll be merged into the PDF when issued.
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : 'Add ASI'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
